/**
 * Helpers around the canonical `landing-categories.json` surface.
 *
 * Phase 9 reconciled the 3-way drift in App.tsx (`categoryImageMap`,
 * `LandingPage.categories`, `CheckoutPage.categories`) onto the single
 * canonical 6-item list. Phase K+2 reads from that list here.
 */

import { isComingSoon, isLive, withBoxSuffix } from '@brandbox/content-schema';
import type { FlowConfig } from '../types';
import type { CategoryItem } from './content';

/**
 * Humanize a flow/category id into a display string.
 *
 * N5b (owner rule "box naming must be admin-driven, not hardcoded"): the
 * `valentine` → "VALENTINE'S" and `eventkit`/`event-kit` → "EVENT KIT"
 * special-cases were REMOVED. They were hardcoded id-literals that ignored
 * every admin field and were exactly what the owner flagged. `getCategoryName`
 * is now the pure humanized-id fallback only — used as the LAST link in the
 * `resolveBoxName` chain (category label → flow title → humanized id).
 *
 * `.replace('-', ' ')` deliberately replaces only the FIRST hyphen, preserving
 * the legacy behavior byte-for-byte (`'spring'` → `'SPRING'`, `'world-cup'` →
 * `'WORLD CUP'`).
 */
export function getCategoryName(id: string): string {
  return id.replace('-', ' ').toUpperCase();
}

/**
 * N5b — resolve the admin-editable display name for a box (its public
 * "<NAME> BOX" header / cart heading source).
 *
 * Owner rule: box naming must come from an admin-editable source, NOT the id.
 * The resolution chain, in priority order:
 *
 *   1. the matching category's `label` (the `categories` content surface,
 *      admin-editable in the N3c Categories editor) — e.g. `spring` → "SPRING",
 *      `office` → "OFFICE";
 *   2. the flow's `title` (admin-editable in the FlowEditor) — covers a box
 *      that has no curated category entry yet;
 *   3. the humanized id (`getCategoryName`) — the graceful last resort so a
 *      brand-new box still gets a readable name.
 *
 * The result is uppercased to PRESERVE the exact current casing. It is the
 * BARE name — no " BOX" suffix. Use `resolveBoxDisplayName` for anything a
 * customer reads.
 *
 * A blank/whitespace-only label or title is skipped (treated as absent) so an
 * empty admin field never blanks the name — we fall through to the next link.
 */
export function resolveBoxName(
  id: string,
  categories: readonly CategoryItem[],
  flows: Record<string, FlowConfig>,
): string {
  const categoryLabel = categories.find((c) => c.id === id)?.label;
  if (categoryLabel && categoryLabel.trim().length > 0) {
    return categoryLabel.toUpperCase();
  }
  const flowTitle = flows[id]?.title;
  if (flowTitle && flowTitle.trim().length > 0) {
    return flowTitle.toUpperCase();
  }
  return getCategoryName(id);
}

/**
 * Chapter 10 (F22) — the name a CUSTOMER reads for a box: the resolved name
 * plus the word BOX, added only when the name does not already end in it.
 *
 * The three places that name a box (landing tile, clear-cart box switch,
 * preview auto-open) each used to spell `` `${resolveBoxName(...)} BOX` ``
 * themselves. That is correct for `spring`/`office`, whose curated labels are
 * "SPRING"/"OFFICE", and wrong for every box created in the Creation Lab: those
 * have no curated entry, fall through to the FLOW TITLE, and an operator
 * naming a box "TeamBuilding Box" shipped "TEAMBUILDING BOX BOX" all the way
 * into the proposal email. The rule now lives in ONE place —
 * `withBoxSuffix` (@brandbox/content-schema) — so no caller can re-introduce
 * the double.
 */
export function resolveBoxDisplayName(
  id: string,
  categories: readonly CategoryItem[],
  flows: Record<string, FlowConfig>,
): string {
  return withBoxSuffix(resolveBoxName(id, categories, flows));
}

/**
 * Drop-in replacement for the legacy `categoryImageMap` lookup.
 *
 * N6c — the dead `worldcup`/`eventkit` → hyphenated alias-key branches were
 * removed. Those ids were dropped from `landing-categories.json` in M+5.7 (the
 * coming-soon row is now driven by admin visibility flips), so the branches
 * never fired, and nothing ever read the hyphenated alias keys.
 */
export function buildCategoryImageMap(categories: CategoryItem[]): Record<string, string> {
  const map: Record<string, string> = {};
  for (const c of categories) {
    map[c.id] = c.image;
  }
  return map;
}

/**
 * The 4-card hero grid in App.tsx slices the canonical list down to the
 * first 4 entries (sorted by `order`).
 */
export function getHeroGridCategories(categories: CategoryItem[]): CategoryItem[] {
  return [...categories]
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .slice(0, 4);
}

/** The Box-Themes grid uses the same 4-card slice. */
export const getBoxThemesCategories = getHeroGridCategories;

/**
 * M+5.5 Phase 4 — preview-mode helper that returns the canonical hero
 * categories PLUS an appended preview tile (the in-progress box the
 * admin is editing). The appended tile lands at the end of the grid,
 * so the operator sees their box appear "below the existing ones" as
 * they edit. Used only when `isPreview === true` and the in-flight
 * flow's id doesn't match any existing category.
 *
 * Production rendering (without `?preview=1`) keeps calling
 * `getHeroGridCategories` directly — bit-for-bit identical to the
 * frozen visual baseline (briefing Constraint #1).
 */
export function getHeroGridCategoriesWithPreview(
  categories: CategoryItem[],
  previewTile: CategoryItem | null,
): CategoryItem[] {
  const hero = getHeroGridCategories(categories);
  if (!previewTile) return hero;
  // Avoid duplicate if the operator is editing one of the canonical
  // categories (in which case their edits already feed into the
  // existing tile via the `flows` registry).
  if (hero.some((c) => c.id === previewTile.id)) return hero;
  return [...hero, previewTile];
}

// ---------------------------------------------------------------------------
// Slice 2 pivot (M+5.7) — visibility-driven append
// ---------------------------------------------------------------------------

/**
 * Public projection of a Cosmos flow doc — only the fields the public
 * landing needs to render a tile. Kept narrow so we don't drag the full
 * schema into apps/public.
 */
export interface FlowVisibilityProjection {
  /** Cosmos doc id, e.g. "bbq-box". */
  id: string;
  /** Display title, used as the tile label uppercased. */
  title: string;
  /** Either 'coming-soon' or 'live' — never 'hidden' (filtered by API). */
  visibility: 'coming-soon' | 'live';
  /** Optional hero image — falls back to a placeholder when absent. */
  heroImage?: string;
}

/**
 * Default placeholder when a flow has no hero image set yet.
 *
 * M+5.7: switched to `products_staged.webp` (which is already in the repo
 * and used to be the static fallback for the now-removed
 * emergency/onboarding entries) so admin-created coming-soon tiles
 * without a hero land on a real product photo rather than the never-
 * existed `/images/general/placeholder.webp`.
 */
export const DEFAULT_FLOW_HERO_IMAGE = '/images/general/products_staged.webp';

/**
 * Project a flow into a `CategoryItem` for tile rendering. The tile is
 * clickable iff the flow is `live`; coming-soon flows render with the
 * "COMING SOON" pill exactly like categories.json's `isAvailable: false`
 * entries (constraint: no new visual primitives — Slice 2 reuses the
 * existing tile styles).
 */
export function flowToCategoryItem(flow: FlowVisibilityProjection): CategoryItem {
  return {
    id: flow.id,
    label: (flow.title ?? flow.id).toUpperCase(),
    image: flow.heroImage ?? DEFAULT_FLOW_HERO_IMAGE,
    isAvailable: isLive(flow),
    // Append at the end of the existing row — 9000 keeps us comfortably
    // past the static categories (whose `order` runs 0..5).
    order: 9000,
  };
}

/**
 * Return the dynamic flows from `flows` that should append to the main
 * hero/category carousel: every flow with `visibility: 'live'` whose id
 * is NOT already represented in the static `landing-categories.json`
 * (`existing`).
 *
 * Empty-flows case: returns `[]` — the caller renders the static
 * categories unchanged (briefing Constraint #1).
 */
export function getLiveAppendCategories(
  existing: CategoryItem[],
  flows: FlowVisibilityProjection[],
): CategoryItem[] {
  const knownIds = new Set(existing.map((c) => c.id));
  return flows
    .filter((f) => isLive(f) && !knownIds.has(f.id))
    .map(flowToCategoryItem);
}

/**
 * M+5.7: overlay admin-driven hero images onto the static category list.
 *
 * For every static category (e.g. `spring`, `office`), if the admin's
 * Cosmos doc for the SAME id reports `visibility: 'live'` with a
 * non-empty `heroImage`, the static `image` is replaced by the admin's
 * value. When the admin hasn't uploaded a hero yet (or set visibility to
 * something other than `live`), the static `image` stays — that's our
 * safety net so a brand-new admin install with empty heroes doesn't
 * blank out the production landing.
 *
 * The label / order / isAvailable fields are NEVER overridden — those
 * stay on the JSON config, which is the operator's "I really mean it"
 * surface. Admin-driven dynamic flows that aren't in the static list
 * still flow through `getLiveAppendCategories` separately.
 */
export function overlayAdminHeroImages(
  existing: CategoryItem[],
  flows: FlowVisibilityProjection[],
): CategoryItem[] {
  const byId = new Map<string, FlowVisibilityProjection>();
  for (const f of flows) {
    if (isLive(f) && f.heroImage && f.heroImage.length > 0) {
      byId.set(f.id, f);
    }
  }
  return existing.map((c) => {
    const match = byId.get(c.id);
    if (!match || !match.heroImage) return c;
    return { ...c, image: match.heroImage };
  });
}

/**
 * Return the dynamic flows that should appear in the Coming Soon row:
 * every flow with `visibility: 'coming-soon'` whose id is NOT in the
 * static `landing-categories.json`. The static list's coming-soon
 * entries stay where they are (in the hero grid as greyed tiles) —
 * the new row holds ONLY the dynamic additions.
 *
 * Empty case: returns `[]` so the consumer can skip rendering the row
 * entirely and the page stays byte-identical to the pre-Slice-2 build.
 */
export function getComingSoonAppendCategories(
  existing: CategoryItem[],
  flows: FlowVisibilityProjection[],
): CategoryItem[] {
  const knownIds = new Set(existing.map((c) => c.id));
  return flows
    .filter((f) => isComingSoon(f) && !knownIds.has(f.id))
    .map(flowToCategoryItem);
}

// ---------------------------------------------------------------------------
// N5 owner rule (2026-06-09) — "no tile without a backing flow"
// ---------------------------------------------------------------------------
//
// Owner rule: a box tile / card / button may appear ANYWHERE on the public
// landing ONLY if its `id` matches a current, non-hidden flow in the flows
// registry. The LIVE `categories` content surface (Cosmos, stale seed) can
// carry phantom entries — ids with no backing admin flow (e.g. `eventkit`,
// `worldcup`, `emergency`, `onboarding`) — and those must NEVER render.
//
// The flows registry is the single source of truth for WHICH ids may render.
// The `categories` surface may still supply curated hero image / label /
// order for ids that DO have a backing flow (via `overlayAdminHeroImages`),
// but it may not introduce a tile for an id the registry doesn't know about.
//
// Fallback / empty-registry case: when `flows` is empty (the content API is
// unreachable or hasn't resolved yet), we have NO knowledge of the registry,
// so pruning would risk blanking the landing. In that case we pass the input
// through unchanged — the static `landing-categories.json` fallback (spring +
// office) keeps rendering byte-for-byte identically (briefing Constraint #1).
// We only enforce the rule once we actually know the registry.

/** Set of every id the flows registry vouches for. */
function backingFlowIds(flows: readonly FlowVisibilityProjection[]): Set<string> {
  return new Set(flows.map((f) => f.id));
}

/**
 * Filter a `categories`-surface list down to the entries whose id is backed
 * by a real, non-hidden flow. Phantom entries (no matching flow) are dropped.
 *
 * `flows` MUST already be the non-hidden projection (the caller filters
 * `visibility !== 'hidden'` before passing it here) — so any id present in
 * `flows` is, by construction, a live or coming-soon box that legitimately
 * earns a tile.
 *
 * Empty `flows` → return the input unchanged (fallback guarantee above).
 */
export function filterCategoriesToFlowBacked(
  categories: readonly CategoryItem[],
  flows: readonly FlowVisibilityProjection[],
): CategoryItem[] {
  if (flows.length === 0) return [...categories];
  const allowed = backingFlowIds(flows);
  return categories.filter((c) => allowed.has(c.id));
}

/**
 * Same rule for the Build-Your-Own button row (each button targets an id via
 * `onSelectCategory(btn.id)`, so it's a clickable box surface). Generic over
 * the button shape so the caller keeps whatever extra fields it carries.
 *
 * Empty `flows` → return the input unchanged (fallback guarantee above).
 */
export function filterButtonsToFlowBacked<T extends { id: string }>(
  buttons: readonly T[],
  flows: readonly FlowVisibilityProjection[],
): T[] {
  if (flows.length === 0) return [...buttons];
  const allowed = backingFlowIds(flows);
  return buttons.filter((b) => allowed.has(b.id));
}

// ---------------------------------------------------------------------------
// Slice 5 of the pivot — preview tile in Coming Soon row
// ---------------------------------------------------------------------------

/**
 * Merge the admin's in-flight preview tile into the Coming Soon row.
 *
 * Inputs:
 *   - `base` — the API-driven coming-soon entries (output of
 *     `getComingSoonAppendCategories`).
 *   - `previewTile` — the synthesized tile for the flow the admin is
 *     editing in the preview iframe; `null` outside preview mode.
 *
 * Behavior:
 *   - `previewTile == null` → return `base` unchanged. Production
 *     rendering for real customers stays bit-for-bit identical.
 *   - `previewTile.id` already in `base` → de-dupe; return `base`.
 *   - Otherwise → append the preview tile at the end.
 *
 * Slice 5 trade-off: the preview tile renders in the Coming Soon row
 * regardless of the in-flight flow's `visibility` field. The preview
 * is for editorial work; the coming-soon styling (greyed pill, not
 * clickable) makes the tile read as unmistakably "draft" so the
 * operator never confuses preview with shipped.
 */
export function withPreviewTileInComingSoonRow(
  base: readonly CategoryItem[],
  previewTile: CategoryItem | null,
): CategoryItem[] {
  if (!previewTile) return [...base];
  if (base.some((c) => c.id === previewTile.id)) return [...base];
  return [...base, previewTile];
}
